import { useEffect, useState, type FormEvent } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { api, ApiError } from '../api.ts';
import { PAYMENT_ERRORS } from '../stripeClient.ts';

export interface SavedCard {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

export const BRANDS: Record<string, string> = { visa: 'Visa', mastercard: 'Mastercard', amex: 'American Express', cb: 'Carte Bancaire' };
export const cardLabel = (c: SavedCard) => `${BRANDS[c.brand] ?? c.brand} •••• ${c.last4} · exp. ${String(c.expMonth).padStart(2, '0')}/${String(c.expYear).slice(-2)}`;

function Form({ organizationId, setupIntentId, onSaved, onCancel }: { organizationId: string; setupIntentId: string; onSaved: (c: SavedCard) => void; onCancel?: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true);
    setError(null);
    // La carte est saisie dans un champ hébergé par Stripe : Mon Extra n'en voit jamais le numéro.
    const result = await stripe.confirmSetup({ elements, redirect: 'if_required' });
    if (result.error) {
      setError(result.error.message ?? 'La carte n’a pas pu être enregistrée.');
      setBusy(false);
      return;
    }
    try {
      const saved = await api.post<{ card: SavedCard }>('/payments/org/card', { organizationId, setupIntentId });
      onSaved(saved.card);
    } catch (err) {
      setError(err instanceof ApiError ? PAYMENT_ERRORS[err.code] ?? 'La carte n’a pas pu être enregistrée.' : 'La carte n’a pas pu être enregistrée.');
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <PaymentElement options={{ layout: 'tabs' }} />
      {error && <div className="error-banner">{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary btn-sm" disabled={!stripe || busy}>{busy ? 'Enregistrement…' : 'Enregistrer la carte'}</button>
        {onCancel && <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}>Annuler</button>}
      </div>
    </form>
  );
}

/** Enregistre une carte pour l'établissement. Aucun débit : le montant est seulement bloqué 48 h avant la mission. */
export function CardSetup({ organizationId, onSaved, onCancel }: { organizationId: string; onSaved: (c: SavedCard) => void; onCancel?: () => void }) {
  const [state, setState] = useState<{ stripe: Promise<Stripe | null>; clientSecret: string; setupIntentId: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .post<{ clientSecret: string; setupIntentId: string; publishableKey: string }>('/payments/org/setup-intent', { organizationId })
      .then((r) => setState({ stripe: loadStripe(r.publishableKey, { locale: 'fr' }), clientSecret: r.clientSecret, setupIntentId: r.setupIntentId }))
      .catch((err) => setError(err instanceof ApiError ? PAYMENT_ERRORS[err.code] ?? 'Le formulaire de carte est indisponible.' : 'Le formulaire de carte est indisponible.'));
  }, [organizationId]);

  if (error) return <div className="badge badge-pending" style={{ padding: '10px 14px', lineHeight: 1.5 }}>{error}</div>;
  if (!state) return <div className="empty-state">Chargement du formulaire sécurisé…</div>;
  return (
    <Elements stripe={state.stripe} options={{ clientSecret: state.clientSecret, locale: 'fr', appearance: { variables: { colorPrimary: '#d9622b', borderRadius: '12px' } } }}>
      <Form organizationId={organizationId} setupIntentId={state.setupIntentId} onSaved={onSaved} onCancel={onCancel} />
    </Elements>
  );
}
